import type { Automation } from "@repo/app-types/entities/automation";
import { ButtonVariant } from "@repo/ui/Buttons/button";
import { iconButton } from "@repo/ui/Buttons/iconButton";
import { menuStyle } from "@repo/ui/Menu/menuStyle";
import { popoverStyle } from "@repo/ui/popover/style";
import {
  IconCalendar,
  IconClock,
  IconInfoCircle,
  IconPlayerPlay,
  IconUser,
} from "@tabler/icons-react";
import { DateTime } from "luxon";
import type { FC, ReactNode } from "react";
import { Button, Dialog, DialogTrigger, Popover } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { formatRelativeTime } from "../../hooks/useDraftSaveStatus.js";
import { describeAutomationNextRun } from "./automationTriggerSummary.js";

function capitalizeFirst(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export interface AutomationInfoPopoverProps {
  automation: Automation;
  createdByName: string | undefined;
  /** Opens the popover initially (for stories/tests). */
  defaultOpen?: boolean;
}

/**
 * Header info button + popover summarizing the automation: when its
 * configuration was last saved, the next scheduled run, and who created it and
 * when. Presentational — the creator's name is resolved by the caller.
 */
export const AutomationInfoPopover: FC<AutomationInfoPopoverProps> = (
  props,
) => {
  const { automation, createdByName, defaultOpen } = props;

  return (
    <DialogTrigger defaultOpen={defaultOpen}>
      <Button
        aria-label="Automation details"
        className={twMerge(
          iconButton({ variant: ButtonVariant.Tertiary }),
          "text-muted-foreground",
        )}
      >
        <IconInfoCircle className="size-5" />
      </Button>

      <Popover placement="bottom end" className={popoverStyle().popover()}>
        <Dialog className="outline-hidden" aria-label="Automation details">
          <div className="w-64 p-1 text-sm">
            <InfoSection label="Last saved">
              <InfoRow icon={<IconClock className="size-4" />}>
                {capitalizeFirst(formatRelativeTime(automation.last_saved_at))}
              </InfoRow>
            </InfoSection>

            <InfoSection label="Next run">
              <InfoRow icon={<IconPlayerPlay className="size-4" />}>
                {describeAutomationNextRun(automation)}
              </InfoRow>
            </InfoSection>

            <InfoSection label="Created">
              <InfoRow icon={<IconCalendar className="size-4" />}>
                {DateTime.fromISO(automation.created_at).toFormat(
                  "MMM d, yyyy, h:mm a",
                )}
              </InfoRow>
              <InfoRow icon={<IconUser className="size-4" />}>
                {createdByName ?? "—"}
              </InfoRow>
            </InfoSection>
          </div>
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
};

interface InfoSectionProps {
  label: string;
  children: ReactNode;
}

// Faux menu section header and items, styled to match the real Menu primitives
// so the popover reads as a regular menu even though its rows aren't interactive.
const InfoSection: FC<InfoSectionProps> = (props) => {
  const { label, children } = props;
  return (
    <div>
      <div className={menuStyle().sectionHeader()}>{label}</div>
      {children}
    </div>
  );
};

interface InfoRowProps {
  icon: ReactNode;
  children: ReactNode;
}

const InfoRow: FC<InfoRowProps> = (props) => {
  const { icon, children } = props;
  return (
    <div className="flex items-center gap-2 rounded-md px-2 py-1.5 text-foreground">
      <span className="shrink-0 text-muted-foreground">{icon}</span>
      <span className="min-w-0 truncate">{children}</span>
    </div>
  );
};
